import { useState } from "react";
import { Play } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

/**
 * ArticleVideo — inline, click-to-play YouTube embed for blog posts.
 * Uses the YouTube-supplied thumbnail as a poster so the page stays fast,
 * then swaps in a `youtube-nocookie` iframe on click. Styled to feel at
 * home next to the homepage VideoShowcase component.
 */
export default function ArticleVideo({ youtubeId, title, kicker, caption }) {
  const [playing, setPlaying] = useState(false);
  if (!youtubeId) return null;
  const poster = `https://i.ytimg.com/vi/${youtubeId}/maxresdefault.jpg`;

  return (
    <section className="bg-white" data-testid="article-video-section">
      <ScrollReveal>
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          {kicker && (
            <div className="text-[13px] tracking-[0.14em] uppercase text-neutral-500 mb-4">
              {kicker}
            </div>
          )}
          {title && (
            <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-[1.08] max-w-3xl mb-10">
              {title}
            </h2>
          )}
          <div className="relative w-full overflow-hidden rounded-none bg-neutral-900" style={{ aspectRatio: "16 / 9" }}>
            {playing ? (
              <iframe
                title={title || "Video"}
                src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="article-video-iframe"
              />
            ) : (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                className="group absolute inset-0 w-full h-full block"
                aria-label="Play video"
                data-testid="article-video-play-btn"
              >
                <img
                  src={poster}
                  alt={title || "Video thumbnail"}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                  onError={(e) => {
                    // Fallback to hqdefault if maxresdefault isn't available
                    if (!e.currentTarget.dataset.fallback) {
                      e.currentTarget.dataset.fallback = "1";
                      e.currentTarget.src = `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
                    }
                  }}
                />
                <div className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-white text-black shadow-xl group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 md:w-10 md:h-10 fill-current translate-x-[2px]" />
                  </span>
                </div>
              </button>
            )}
          </div>
          {caption && (
            <p className="mt-4 text-sm text-neutral-500 max-w-3xl" data-testid="article-video-caption">
              {caption}
            </p>
          )}
        </div>
      </ScrollReveal>
    </section>
  );
}
